import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import {
  MagnifyingGlass,
  Funnel,
  Heart,
  Headphones,
  Play,
  Pause,
  UserPlus,
  CheckCircle,
  Clock,
  UserCircle,
  Radio,
  ArrowRight,
} from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { motion } from 'framer-motion';

const searchTexts = {
  de: {
    title: 'Suche',
    subtitle: 'Finde User oder Radiosender weltweit',
    tabUsers: 'User',
    tabStations: 'Radiosender',
    userPlaceholder: 'Benutzername, Land oder Musikrichtung suchen...',
    stationPlaceholder: 'Sender, Länder oder Städte suchen...',
    filters: 'Filter:',
    allGenres: 'Alle Genres',
    allCountries: 'Alle Länder',
    allLanguages: 'Alle Sprachen',
    sortBy: 'Sortieren nach',
    mostPopular: 'Beliebteste',
    newest: 'Neueste',
    alphabetical: 'A-Z',
    searching: 'Suche läuft...',
    foundPrefix: 'Gefunden:',
    foundSuffix: 'Sender',
    noStationsTitle: 'Keine Sender gefunden',
    noStationsText: 'Versuche deine Suche oder Filter anzupassen',
    loading: 'Lädt...',
    loadMore: 'Mehr Sender laden',
    plays: 'Wiedergaben',
    users: 'User',
    userSearchHint: 'Gefundene User aus der WORLDofRADIO-Community',
    noUsersFound: 'Keine User gefunden.',
    addFriend: 'Freund hinzufügen & Chat freischalten',
    requestSent: 'Anfrage gesendet',
    friends: 'Freunde',
    incomingRequest: 'Anfrage erhalten',
    searchLoginHint: 'Melde dich an, um User zu finden und Freundschaftsanfragen zu senden.',
    musicGenres: 'Musikrichtungen',
    noCountry: 'Kein Land angegeben',
    languageEnglish: 'Englisch',
    languageSpanish: 'Spanisch',
    languageFrench: 'Französisch',
    languageGerman: 'Deutsch',
    languageItalian: 'Italienisch',
    languagePortuguese: 'Portugiesisch',
    languageJapanese: 'Japanisch',
    languageKorean: 'Koreanisch',
    languageChinese: 'Chinesisch',
    languageHindi: 'Hindi',
    languageArabic: 'Arabisch',
    languageRussian: 'Russisch',
    languageDutch: 'Niederländisch',
    languagePolish: 'Polnisch',
    languageTurkish: 'Türkisch',
  },

  en: {
    title: 'Search',
    subtitle: 'Find users or radio stations worldwide',
    tabUsers: 'Users',
    tabStations: 'Radio stations',
    userPlaceholder: 'Search username, country or music genre...',
    stationPlaceholder: 'Search stations, countries or cities...',
    filters: 'Filters:',
    allGenres: 'All Genres',
    allCountries: 'All Countries',
    allLanguages: 'All Languages',
    sortBy: 'Sort by',
    mostPopular: 'Most Popular',
    newest: 'Newest',
    alphabetical: 'A-Z',
    searching: 'Searching...',
    foundPrefix: 'Found',
    foundSuffix: 'stations',
    noStationsTitle: 'No stations found',
    noStationsText: 'Try adjusting your search or filters',
    loading: 'Loading...',
    loadMore: 'Load More Stations',
    plays: 'plays',
    users: 'Users',
    userSearchHint: 'Found users from the WORLDofRADIO community',
    noUsersFound: 'No users found.',
    addFriend: 'Add friend & unlock chat',
    requestSent: 'Request sent',
    friends: 'Friends',
    incomingRequest: 'Request received',
    searchLoginHint: 'Log in to find users and send friend requests.',
    musicGenres: 'Music genres',
    noCountry: 'No country provided',
    languageEnglish: 'English',
    languageSpanish: 'Spanish',
    languageFrench: 'French',
    languageGerman: 'German',
    languageItalian: 'Italian',
    languagePortuguese: 'Portuguese',
    languageJapanese: 'Japanese',
    languageKorean: 'Korean',
    languageChinese: 'Chinese',
    languageHindi: 'Hindi',
    languageArabic: 'Arabic',
    languageRussian: 'Russian',
    languageDutch: 'Dutch',
    languagePolish: 'Polish',
    languageTurkish: 'Turkish',
  },

  no: {
    title: 'Søk',
    subtitle: 'Finn brukere eller radiostasjoner over hele verden',
    tabUsers: 'Brukere',
    tabStations: 'Radiostasjoner',
    userPlaceholder: 'Søk etter brukernavn, land eller musikksjanger...',
    stationPlaceholder: 'Søk etter stasjoner, land eller byer...',
    filters: 'Filtre:',
    allGenres: 'Alle sjangre',
    allCountries: 'Alle land',
    allLanguages: 'Alle språk',
    sortBy: 'Sorter etter',
    mostPopular: 'Mest populære',
    newest: 'Nyeste',
    alphabetical: 'A-Z',
    searching: 'Søker...',
    foundPrefix: 'Fant',
    foundSuffix: 'stasjoner',
    noStationsTitle: 'Ingen stasjoner funnet',
    noStationsText: 'Prøv å endre søket eller filtrene',
    loading: 'Laster...',
    loadMore: 'Last inn flere stasjoner',
    plays: 'avspillinger',
    users: 'Brukere',
    userSearchHint: 'Fant brukere fra WORLDofRADIO-fellesskapet',
    noUsersFound: 'Ingen brukere funnet.',
    addFriend: 'Legg til venn og lås opp chat',
    requestSent: 'Forespørsel sendt',
    friends: 'Venner',
    incomingRequest: 'Forespørsel mottatt',
    searchLoginHint: 'Logg inn for å finne brukere og sende venneforespørsler.',
    musicGenres: 'Musikksjangre',
    noCountry: 'Ingen land angitt',
    languageEnglish: 'Engelsk',
    languageSpanish: 'Spansk',
    languageFrench: 'Fransk',
    languageGerman: 'Tysk',
    languageItalian: 'Italiensk',
    languagePortuguese: 'Portugisisk',
    languageJapanese: 'Japansk',
    languageKorean: 'Koreansk',
    languageChinese: 'Kinesisk',
    languageHindi: 'Hindi',
    languageArabic: 'Arabisk',
    languageRussian: 'Russisk',
    languageDutch: 'Nederlandsk',
    languagePolish: 'Polsk',
    languageTurkish: 'Tyrkisk',
  },

  ko: {
    title: '검색',
    subtitle: '전 세계의 사용자 또는 라디오 방송국을 찾으세요',
    tabUsers: '사용자',
    tabStations: '라디오 방송국',
    userPlaceholder: '사용자 이름, 국가 또는 음악 장르 검색...',
    stationPlaceholder: '방송국, 국가 또는 도시 검색...',
    filters: '필터:',
    allGenres: '전체 장르',
    allCountries: '전체 국가',
    allLanguages: '전체 언어',
    sortBy: '정렬 기준',
    mostPopular: '인기순',
    newest: '최신순',
    alphabetical: 'A-Z',
    searching: '검색 중...',
    foundPrefix: '검색 결과',
    foundSuffix: '개 방송국',
    noStationsTitle: '방송국을 찾을 수 없습니다',
    noStationsText: '검색어 또는 필터를 조정해 보세요',
    loading: '로딩 중...',
    loadMore: '방송국 더 보기',
    plays: '재생',
    users: '사용자',
    userSearchHint: 'WORLDofRADIO 커뮤니티에서 찾은 사용자',
    noUsersFound: '사용자를 찾을 수 없습니다.',
    addFriend: '친구 추가 및 채팅 열기',
    requestSent: '요청 전송됨',
    friends: '친구',
    incomingRequest: '요청 받음',
    searchLoginHint: '사용자를 찾고 친구 요청을 보내려면 로그인하세요.',
    musicGenres: '음악 장르',
    noCountry: '국가 정보 없음',
    languageEnglish: '영어',
    languageSpanish: '스페인어',
    languageFrench: '프랑스어',
    languageGerman: '독일어',
    languageItalian: '이탈리아어',
    languagePortuguese: '포르투갈어',
    languageJapanese: '일본어',
    languageKorean: '한국어',
    languageChinese: '중국어',
    languageHindi: '힌디어',
    languageArabic: '아랍어',
    languageRussian: '러시아어',
    languageDutch: '네덜란드어',
    languagePolish: '폴란드어',
    languageTurkish: '터키어',
  },

  fi: {
    title: 'Haku',
    subtitle: 'Etsi käyttäjiä tai radioasemia maailmanlaajuisesti',
    tabUsers: 'Käyttäjät',
    tabStations: 'Radioasemat',
    userPlaceholder: 'Etsi käyttäjänimeä, maata tai musiikkityyliä...',
    stationPlaceholder: 'Etsi asemia, maita tai kaupunkeja...',
    filters: 'Suodattimet:',
    allGenres: 'Kaikki genret',
    allCountries: 'Kaikki maat',
    allLanguages: 'Kaikki kielet',
    sortBy: 'Lajittele',
    mostPopular: 'Suosituimmat',
    newest: 'Uusimmat',
    alphabetical: 'A-Z',
    searching: 'Haetaan...',
    foundPrefix: 'Löytyi',
    foundSuffix: 'asemaa',
    noStationsTitle: 'Asemia ei löytynyt',
    noStationsText: 'Kokeile muuttaa hakua tai suodattimia',
    loading: 'Ladataan...',
    loadMore: 'Lataa lisää asemia',
    plays: 'toistoa',
    users: 'Käyttäjät',
    userSearchHint: 'Löydetyt käyttäjät WORLDofRADIO-yhteisöstä',
    noUsersFound: 'Käyttäjiä ei löytynyt.',
    addFriend: 'Lisää ystävä ja avaa chat',
    requestSent: 'Pyyntö lähetetty',
    friends: 'Ystävät',
    incomingRequest: 'Pyyntö vastaanotettu',
    searchLoginHint: 'Kirjaudu sisään löytääksesi käyttäjiä ja lähettääksesi ystäväpyyntöjä.',
    musicGenres: 'Musiikkityylit',
    noCountry: 'Maata ei annettu',
    languageEnglish: 'Englanti',
    languageSpanish: 'Espanja',
    languageFrench: 'Ranska',
    languageGerman: 'Saksa',
    languageItalian: 'Italia',
    languagePortuguese: 'Portugali',
    languageJapanese: 'Japani',
    languageKorean: 'Korea',
    languageChinese: 'Kiina',
    languageHindi: 'Hindi',
    languageArabic: 'Arabia',
    languageRussian: 'Venäjä',
    languageDutch: 'Hollanti',
    languagePolish: 'Puola',
    languageTurkish: 'Turkki',
  },
};

const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialMode = searchParams.get('type') === 'stations' ? 'stations' : 'users';

  const [activeMode, setActiveMode] = useState(initialMode);
  const [stations, setStations] = useState([]);
  const [genres, setGenres] = useState([]);
  const [countries, setCountries] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [requestLoadingId, setRequestLoadingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const LIMIT = 30;

  const [filters, setFilters] = useState({
    search: searchParams.get('q') || '',
    genre: searchParams.get('genre') || 'all',
    country: searchParams.get('country') || 'all',
    language: searchParams.get('language') || 'all',
    sort: searchParams.get('sort') || 'popularity',
  });

  const navigate = useNavigate();
  const { user } = useAuth();
  const { playStation, currentStation, isPlaying } = useAudioPlayer();
  const { language } = useLanguage();

  const text = searchTexts[language] || searchTexts.en;

  useEffect(() => {
    loadMetadata();

    if (user) {
      loadFavorites();
      searchUsers(filters.search);
    } else {
      setUsers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (activeMode === 'stations') {
      setOffset(0);
      setHasMore(true);
      performSearch(0, true);
    }

    if (activeMode === 'users' && user) {
      searchUsers(filters.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeMode]);

  const loadMetadata = async () => {
    try {
      const [genresRes, countriesRes] = await Promise.all([
        api.getGenres(),
        api.getCountries(),
      ]);

      setGenres(genresRes.data);
      setCountries(countriesRes.data);
    } catch (error) {
      console.error('Failed to load metadata:', error);
    }
  };

  const loadFavorites = async () => {
    try {
      const res = await api.getFavorites();
      setFavorites(res.data.map((s) => s.station_id));
    } catch (error) {
      console.error('Failed to load favorites:', error);
    }
  };

  const searchUsers = async (queryValue = '') => {
    if (!user) {
      setUsers([]);
      return;
    }

    try {
      setUsersLoading(true);
      const response = await api.searchUsers(queryValue, 30);
      setUsers(response.data || []);
    } catch (error) {
      console.error('User search failed:', error);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const performSearch = async (currentOffset = 0, reset = false) => {
    if (reset) setLoading(true);
    else setLoadingMore(true);

    try {
      const params = { limit: LIMIT, offset: currentOffset };

      if (filters.search) params.search = filters.search;
      if (filters.genre && filters.genre !== 'all') params.genre = filters.genre;
      if (filters.country && filters.country !== 'all') params.country = filters.country;
      if (filters.language && filters.language !== 'all') params.language = filters.language;
      if (filters.sort) params.sort = filters.sort;

      const res = await api.getStations(params);

      if (reset) {
        setStations(res.data);
      } else {
        setStations((prev) => [...prev, ...res.data]);
      }

      setHasMore(res.data.length === LIMIT);
      setOffset(currentOffset + res.data.length);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const updateUrlParams = (nextFilters, nextMode = activeMode) => {
    const params = {};

    params.type = nextMode;

    if (nextFilters.search) params.q = nextFilters.search;

    if (nextMode === 'stations') {
      if (nextFilters.genre && nextFilters.genre !== 'all') params.genre = nextFilters.genre;
      if (nextFilters.country && nextFilters.country !== 'all') params.country = nextFilters.country;
      if (nextFilters.language && nextFilters.language !== 'all') params.language = nextFilters.language;
      if (nextFilters.sort) params.sort = nextFilters.sort;
    }

    setSearchParams(params);
  };

  const handleModeChange = (mode) => {
    setActiveMode(mode);
    updateUrlParams(filters, mode);
  };

  const loadMore = () => {
    performSearch(offset, false);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    updateUrlParams(newFilters);
  };

  const handleFavorite = async (stationId) => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      if (favorites.includes(stationId)) {
        await api.removeFavorite(stationId);
        setFavorites(favorites.filter((id) => id !== stationId));
      } else {
        await api.addFavorite(stationId);
        setFavorites([...favorites, stationId]);
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const openUserProfile = (foundUser) => {
    if (!foundUser?.username) return;
    navigate(`/user/${foundUser.username}`);
  };

  const handleSendFriendRequest = async (event, targetUserId) => {
    event.stopPropagation();

    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setRequestLoadingId(targetUserId);
      await api.sendFriendRequest(targetUserId);

      setUsers((prev) =>
        prev.map((foundUser) =>
          foundUser.user_id === targetUserId
            ? { ...foundUser, friendship_status: 'outgoing_pending' }
            : foundUser
        )
      );
    } catch (error) {
      console.error('Failed to send friend request:', error);
    } finally {
      setRequestLoadingId(null);
    }
  };

  const renderResultsText = () => {
    if (loading) return text.searching;

    if (language === 'ko') {
      return `${text.foundPrefix} ${stations.length}${text.foundSuffix}`;
    }

    return `${text.foundPrefix} ${stations.length} ${text.foundSuffix}`;
  };

  const getUserButton = (foundUser) => {
    const status = foundUser.friendship_status;

    if (status === 'friends') {
      return (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            openUserProfile(foundUser);
          }}
          className="inline-flex items-center gap-2 rounded-full bg-cyan-400/10 border border-cyan-400/30 px-4 py-2 text-sm font-bold text-cyan-300 hover:bg-cyan-400/20 transition-colors"
        >
          <CheckCircle size={18} weight="fill" />
          {text.friends}
        </button>
      );
    }

    if (status === 'outgoing_pending') {
      return (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            openUserProfile(foundUser);
          }}
          className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-4 py-2 text-sm font-bold text-gray-400 hover:bg-white/10 transition-colors"
        >
          <Clock size={18} />
          {text.requestSent}
        </button>
      );
    }

    if (status === 'incoming_pending') {
      return (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            navigate('/notifications');
          }}
          className="inline-flex items-center gap-2 rounded-full bg-cyan-400/10 border border-cyan-400/30 px-4 py-2 text-sm font-bold text-cyan-300 hover:bg-cyan-400/20 transition-colors"
        >
          <UserPlus size={18} />
          {text.incomingRequest}
        </button>
      );
    }

    return (
      <button
        type="button"
        onClick={(event) => handleSendFriendRequest(event, foundUser.user_id)}
        disabled={requestLoadingId === foundUser.user_id}
        className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-4 py-2 text-sm font-black text-black hover:bg-cyan-300 transition-colors disabled:opacity-60"
      >
        <UserPlus size={18} weight="bold" />
        {requestLoadingId === foundUser.user_id ? text.loading : text.addFriend}
      </button>
    );
  };

  const renderUserCountry = (foundUser) => {
    return foundUser.country || text.noCountry;
  };

  const renderUserGenres = (foundUser) => {
    if (Array.isArray(foundUser.music_genres) && foundUser.music_genres.length > 0) {
      return foundUser.music_genres.join(', ');
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-[#050505] pb-40 md:pb-28">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="mb-10">
          <h1
            className="text-4xl sm:text-5xl tracking-tighter font-black text-white mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {text.title}
          </h1>

          <p className="text-base text-gray-300">{text.subtitle}</p>
        </div>

        {/* Search Mode Switch */}
        <div className="mb-6 inline-flex rounded-full border border-white/10 bg-black/60 p-1">
          <button
            type="button"
            onClick={() => handleModeChange('users')}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-black transition-all ${activeMode === 'users'
                ? 'bg-cyan-400 text-black shadow-[0_0_14px_rgba(34,211,238,0.35)]'
                : 'text-gray-300 hover:text-cyan-300 hover:bg-white/5'
              }`}
          >
            <UserCircle size={18} weight={activeMode === 'users' ? 'fill' : 'regular'} />
            {text.tabUsers}
          </button>

          <button
            type="button"
            onClick={() => handleModeChange('stations')}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-black transition-all ${activeMode === 'stations'
                ? 'bg-cyan-400 text-black shadow-[0_0_14px_rgba(34,211,238,0.35)]'
                : 'text-gray-300 hover:text-cyan-300 hover:bg-white/5'
              }`}
          >
            <Radio size={18} weight={activeMode === 'stations' ? 'fill' : 'regular'} />
            {text.tabStations}
          </button>
        </div>

        {/* Search & Filters */}
        <div className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-3xl p-6 mb-8">
          <div className="space-y-4">
            <div className="relative">
              <MagnifyingGlass
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              />

              <Input
                data-testid="search-query-input"
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder={
                  activeMode === 'users'
                    ? text.userPlaceholder
                    : text.stationPlaceholder
                }
                className="pl-12 bg-[#0A0E17] border-white/10 text-white h-14 rounded-2xl focus:border-cyan-500 focus:ring-cyan-500/20 text-base"
              />
            </div>

            {activeMode === 'stations' && (
              <>
                <div className="flex items-center gap-3">
                  <Funnel size={20} className="text-cyan-400" />
                  <span className="text-sm font-semibold text-white">
                    {text.filters}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Select
                    value={filters.genre}
                    onValueChange={(val) => handleFilterChange('genre', val)}
                  >
                    <SelectTrigger
                      data-testid="genre-filter"
                      className="bg-[#0A0E17] border-white/10 text-white rounded-xl"
                    >
                      <SelectValue placeholder={text.allGenres} />
                    </SelectTrigger>

                    <SelectContent className="bg-[#0A0E17] border-white/10 text-white">
                      <SelectItem value="all">{text.allGenres}</SelectItem>

                      {genres.map((g) => (
                        <SelectItem key={g.slug} value={g.name}>
                          {g.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={filters.country}
                    onValueChange={(val) => handleFilterChange('country', val)}
                  >
                    <SelectTrigger
                      data-testid="country-filter"
                      className="bg-[#0A0E17] border-white/10 text-white rounded-xl"
                    >
                      <SelectValue placeholder={text.allCountries} />
                    </SelectTrigger>

                    <SelectContent className="bg-[#0A0E17] border-white/10 text-white">
                      <SelectItem value="all">{text.allCountries}</SelectItem>

                      {countries.map((c) => (
                        <SelectItem key={c.code} value={c.name}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={filters.language}
                    onValueChange={(val) => handleFilterChange('language', val)}
                  >
                    <SelectTrigger
                      data-testid="language-filter"
                      className="bg-[#0A0E17] border-white/10 text-white rounded-xl"
                    >
                      <SelectValue placeholder={text.allLanguages} />
                    </SelectTrigger>

                    <SelectContent className="bg-[#0A0E17] border-white/10 text-white">
                      <SelectItem value="all">{text.allLanguages}</SelectItem>
                      <SelectItem value="english">{text.languageEnglish}</SelectItem>
                      <SelectItem value="spanish">{text.languageSpanish}</SelectItem>
                      <SelectItem value="french">{text.languageFrench}</SelectItem>
                      <SelectItem value="german">{text.languageGerman}</SelectItem>
                      <SelectItem value="italian">{text.languageItalian}</SelectItem>
                      <SelectItem value="portuguese">{text.languagePortuguese}</SelectItem>
                      <SelectItem value="japanese">{text.languageJapanese}</SelectItem>
                      <SelectItem value="korean">{text.languageKorean}</SelectItem>
                      <SelectItem value="chinese">{text.languageChinese}</SelectItem>
                      <SelectItem value="hindi">{text.languageHindi}</SelectItem>
                      <SelectItem value="arabic">{text.languageArabic}</SelectItem>
                      <SelectItem value="russian">{text.languageRussian}</SelectItem>
                      <SelectItem value="dutch">{text.languageDutch}</SelectItem>
                      <SelectItem value="polish">{text.languagePolish}</SelectItem>
                      <SelectItem value="turkish">{text.languageTurkish}</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select
                    value={filters.sort}
                    onValueChange={(val) => handleFilterChange('sort', val)}
                  >
                    <SelectTrigger
                      data-testid="sort-filter"
                      className="bg-[#0A0E17] border-white/10 text-white rounded-xl"
                    >
                      <SelectValue placeholder={text.sortBy} />
                    </SelectTrigger>

                    <SelectContent className="bg-[#0A0E17] border-white/10 text-white">
                      <SelectItem value="popularity">{text.mostPopular}</SelectItem>
                      <SelectItem value="newest">{text.newest}</SelectItem>
                      <SelectItem value="alphabetical">{text.alphabetical}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>
        </div>

        {activeMode === 'users' ? (
          <section>
            <div className="flex items-center gap-3 mb-3">
              <UserCircle size={30} weight="duotone" className="text-cyan-400" />
              <h2
                className="text-2xl font-black text-white"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {text.users}
              </h2>
            </div>

            {!user ? (
              <div className="rounded-3xl border border-white/10 bg-black/60 p-6 text-gray-400">
                {text.searchLoginHint}
              </div>
            ) : usersLoading ? (
              <div className="rounded-3xl border border-white/10 bg-black/60 p-6 text-gray-400">
                {text.loading}
              </div>
            ) : users.length === 0 ? (
              <div className="rounded-3xl border border-white/10 bg-black/60 p-6 text-gray-500">
                {text.noUsersFound}
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-500 mb-4">{text.userSearchHint}</p>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {users.map((foundUser) => {
                    const genresText = renderUserGenres(foundUser);

                    return (
                      <div
                        key={foundUser.user_id}
                        role="button"
                        tabIndex={0}
                        onClick={() => openUserProfile(foundUser)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openUserProfile(foundUser);
                          }
                        }}
                        className="group text-left rounded-2xl border border-white/10 bg-[#0A0E17] p-5 hover:border-cyan-400/40 hover:bg-white/[0.04] transition-all cursor-pointer focus:outline-none focus:border-cyan-400/60"
                        aria-label={`${text.openProfile || 'Profil öffnen'}: ${foundUser.username}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-center gap-4 min-w-0">
                            <div className="w-12 h-12 rounded-full bg-cyan-400/15 border border-cyan-400/30 flex items-center justify-center text-cyan-300 font-black flex-shrink-0 overflow-hidden">
                              {foundUser.avatar ? (
                                <img
                                  src={foundUser.avatar}
                                  alt={foundUser.username}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                (foundUser.username || 'U').charAt(0).toUpperCase()
                              )}
                            </div>

                            <div className="min-w-0">
                              <p className="text-white font-bold truncate group-hover:text-cyan-300 transition-colors">
                                @{foundUser.username}
                              </p>

                              <p className="text-sm text-gray-500 truncate">
                                {renderUserCountry(foundUser)}
                              </p>

                              {genresText && (
                                <p className="text-xs text-gray-600 truncate mt-1">
                                  {text.musicGenres}: {genresText}
                                </p>
                              )}
                            </div>
                          </div>

                          <ArrowRight
                            size={20}
                            className="text-gray-700 group-hover:text-cyan-400 transition-colors flex-shrink-0 mt-1"
                          />
                        </div>

                        {foundUser.bio && (
                          <p className="text-sm text-gray-400 mt-4 line-clamp-2">
                            {foundUser.bio}
                          </p>
                        )}

                        <div className="mt-5">{getUserButton(foundUser)}</div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </section>
        ) : (
          <div>
            <div className="mb-6">
              <p className="text-gray-300">{renderResultsText()}</p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-24">
                <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : stations.length === 0 ? (
              <div className="text-center py-24">
                <MagnifyingGlass
                  size={64}
                  weight="duotone"
                  className="text-gray-600 mx-auto mb-6"
                />

                <h3 className="text-xl font-semibold text-white mb-3">
                  {text.noStationsTitle}
                </h3>

                <p className="text-gray-400">{text.noStationsText}</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {stations.map((station) => {
                    const isCurrentlyPlaying =
                      currentStation?.station_id === station.station_id && isPlaying;

                    return (
                      <motion.div
                        key={station.station_id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        data-testid={`search-result-${station.station_id}`}
                        className="bg-[#0A0E17] border border-white/5 rounded-2xl p-6 hover:border-cyan-500/50 hover:shadow-[0_0_20px_rgba(0,255,255,0.15)] hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
                        onClick={() => navigate(`/station/${station.station_id}`)}
                      >
                        <div className="aspect-square rounded-xl overflow-hidden mb-4 relative bg-[#0A0E17]">
                          <img
                            src={
                              station.logo_url ||
                              'https://images.unsplash.com/photo-1773053525998-8cb667020fa7?w=400'
                            }
                            alt={station.name}
                            onError={(e) => {
                              e.target.src =
                                'https://images.unsplash.com/photo-1773053525998-8cb667020fa7?w=400';
                            }}
                            className="w-full h-full object-cover"
                          />

                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4">
                            <button
                              data-testid={`search-play-btn-${station.station_id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                playStation(station);
                              }}
                              className="w-14 h-14 rounded-full bg-cyan-500 hover:bg-cyan-400 text-black flex items-center justify-center hover:shadow-[0_0_20px_rgba(0,255,255,0.5)] transition-all"
                            >
                              {isCurrentlyPlaying ? (
                                <Pause size={24} weight="fill" />
                              ) : (
                                <Play size={24} weight="fill" />
                              )}
                            </button>
                          </div>
                        </div>

                        <div className="flex items-start justify-between mb-2">
                          <h3 className="text-lg font-semibold text-white group-hover:text-cyan-400 transition-colors line-clamp-1">
                            {station.name}
                          </h3>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFavorite(station.station_id);
                            }}
                            className="text-gray-400 hover:text-cyan-400 transition-colors flex-shrink-0 ml-2"
                          >
                            <Heart
                              size={20}
                              weight={
                                favorites.includes(station.station_id)
                                  ? 'fill'
                                  : 'regular'
                              }
                              className={
                                favorites.includes(station.station_id)
                                  ? 'text-cyan-400'
                                  : ''
                              }
                            />
                          </button>
                        </div>

                        <p className="text-sm text-gray-400 mb-2 line-clamp-1">
                          {station.country} • {station.genre}
                        </p>

                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Headphones size={14} />
                          <span>
                            {station.play_count?.toLocaleString() || 0} {text.plays}
                          </span>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {hasMore && (
                  <div className="flex justify-center mt-12">
                    <button
                      data-testid="load-more-btn"
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold rounded-full px-10 py-4 transition-all disabled:opacity-50"
                    >
                      {loadingMore ? text.loading : text.loadMore}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Search;